import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslations } from '@components/context/TranslationContext';
import StatusItem from './StatusItem';
import StepsBreakdown from './StepsBreakdown';
import { normalizeItems } from './helpers';
import {
    DEFAULT_STATUS_LABELS,
    DOT_TONE,
    STATUS_SUMMARY_VARIANT,
} from './constants';

// Design System — StatusSummary
// Compact per-status summary of a recruitment funnel. Two variants:
// `legend` shows the status names on a tinted bar (dashboard legend),
// `counts` shows the per-status applicant counts inline; count items can
// carry a per-step breakdown shown in a core/Tooltip, where steps with a
// `link` navigate to the filtered applicant list. Nothing is mutated.
// Styles: ./index.less is wired into less/ats_style.less (the white-label
// pipeline) — no JSX .less import, per frontend-structure.mdc.

const StatusSummary = ({
    items = [],
    variant = STATUS_SUMMARY_VARIANT.COUNTS,
    withBackground,
    tones = {},
    labels = {},
    className = null,
    ...rest
}) => {
    const { t } = useTranslations();

    // The legend bar is tinted by default; either default is overridable.
    const hasBackground = withBackground ?? variant === STATUS_SUMMARY_VARIANT.LEGEND;
    const normalized = normalizeItems(items, { tones });

    const resolveStatusLabel = (item) =>
        item.label ??
        labels[item.status] ??
        (DEFAULT_STATUS_LABELS[item.status] ? t(DEFAULT_STATUS_LABELS[item.status]) : item.status);

    return (
        <div
            className={classNames(
                'ds-status-summary',
                `ds-status-summary--${variant}`,
                { 'ds-status-summary--with-bg': hasBackground },
                className,
            )}
            {...rest}
        >
            {normalized.map((item, index) => (
                <StatusItem
                    key={item.status || index}
                    item={item}
                    variant={variant}
                    statusLabel={resolveStatusLabel(item)}
                />
            ))}
        </div>
    );
};

StatusSummary.propTypes = {
    /** Statuses to summarise: `{ status, label?, count?, tone?, steps? }`. */
    items: PropTypes.arrayOf(PropTypes.object),
    /** `legend` (status names on a bar) or `counts` (per-status numbers, the default). */
    variant: PropTypes.oneOf(Object.values(STATUS_SUMMARY_VARIANT)),
    /** Tinted container. Defaults to true for `legend`, false for `counts`. */
    withBackground: PropTypes.bool,
    /** Per-status dot tone override map, e.g. `{ no_outcome: 'warning' }`. */
    tones: PropTypes.objectOf(PropTypes.oneOf(Object.values(DOT_TONE))),
    /** Per-status text override map, e.g. `{ no_outcome: 'Open' }`. */
    labels: PropTypes.objectOf(PropTypes.node),
    /** Extra class on the root element. */
    className: PropTypes.string,
};

export default StatusSummary;
export { StatusItem, StepsBreakdown };
export {
    STATUS_SUMMARY_VARIANT,
    OUTCOME_STATUS,
    DOT_TONE,
    DEFAULT_STATUS_TONES,
    DEFAULT_STATUS_LABELS,
} from './constants';
